/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { SetStateAction, useRef, useState } from "react";

import { Range } from "slate";
import styled from "styled-components";
import { useOnClickOutside } from "usehooks-ts";

import { Layer } from "@vienna/common";

import { SlashMenuOption } from "src/components/SlashMenu/slashMenuOptions";
import { SlashOption } from "src/components/SlashMenu/SlashOption";

import { positionSlashMenu } from "./lib/helpers";

interface Props {
  close: () => void;
  handleSelect: () => void;
  displayedOptions: SlashMenuOption[];
  commandRange: Range;
  elementRef: React.RefObject<HTMLDivElement> | undefined;
  inputType: "keyboard" | "mouse";
  setInputType: React.Dispatch<SetStateAction<"keyboard" | "mouse">>;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<SetStateAction<number>>;
  refocusOnEditor: () => void;
}

export function SlashMenu({
  close,
  handleSelect,
  displayedOptions,
  elementRef,
  setInputType,
  inputType,
  selectedIndex,
  setSelectedIndex,
  refocusOnEditor,
}: Props) {
  const slashMenuRef = useRef<HTMLDivElement>(null);

  const [position] = useState(positionSlashMenu(elementRef));

  useOnClickOutside(slashMenuRef, () => close());
  return (
    position && (
      <StyledSlashMenu
        onMouseMove={() => setInputType("mouse")}
        ref={slashMenuRef}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault(); // prevent enter from inserting newline into editor
          handleSelect();
          refocusOnEditor();
        }}
        style={{ top: position.top, left: position.shouldReverse ? position.left - 240 : position.left }}
      >
        {displayedOptions.map((option, index) => (
          <SlashOption
            option={option}
            key={option.description}
            selected={selectedIndex === index}
            index={index}
            setSelectedIndex={setSelectedIndex}
            inputType={inputType}
          />
        ))}
      </StyledSlashMenu>
    )
  );
}

const StyledSlashMenu = styled.div`
  position: absolute;
  // prevents border from showing up as dot when menu has no options left
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.background};
  border: ${({ theme }) => `1px solid ${theme.primary}`};
  font-family: "IBM Plex Mono";
  font-weight: 600;
  max-height: 330px;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: ${Layer.WIN_LAYER};
`;
